import {
  AdConstraint,
  AdGroupConstraint,
  AdsCampaign,
  AdsCampaignConstraint,
  AdsGroup,
  AdsGroupAd,
  Constraints,
  ConstraintType,
  DeviceConstraint,
  FacebookAdsAdSetAd,
  GoogleAdsAdGroupAd,
} from '@betterfunnel/types';
import Autocomplete from '@betterfunnel/ui-core/Autocomplete';
import Badge from '@betterfunnel/ui-core/Badge';
import Button from '@betterfunnel/ui-core/Button';
import ChipsAutocomplete from '@betterfunnel/ui-core/ChipsAutocomplete';
import IconButton from '@betterfunnel/ui-core/IconButton';
import Loader from '@betterfunnel/ui-core/Loader';
import TextField from '@betterfunnel/ui-core/TextField';
import ToggleButton from '@betterfunnel/ui-core/ToggleButton';
import Tooltip from '@betterfunnel/ui-core/Tooltip';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  CheckIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { uid } from 'uid';
import facebookAdsIcon from '../../assets/images/facebookAdsIcon.png';
import googleAdsIcon from '../../assets/images/googleAdsIcon.png';
import timezonesList from '../../assets/timezones.json';
import { DashboardContext } from '../../contexts/DashboardContext';
import { UserContext } from '../../contexts/UserContext';
import {
  getCachedAdGroupAds,
  getCachedAdGroups,
  getCachedCampaigns,
} from '../../services/AdsAccountsService';
import { capitalize, classNames, distinct } from '../../utils';
import { getConstraintTitle } from '../../utils/constraints.utils';
import Breadcrumbs from '../Breadcrumbs';
import { SearchField } from '../SearchField';
import { AdPreview } from './AdPreview';
import { KeywordsConstraintSelectorModal } from './KeywordsConstraintSelectorModal';

const daysOfWeek = [
  { value: 'Mon', title: 'Monday' },
  { value: 'Tue', title: 'Tuesday' },
  { value: 'Wed', title: 'Wednesday' },
  { value: 'Thu', title: 'Thursday' },
  { value: 'Fri', title: 'Friday' },
  { value: 'Sat', title: 'Saturday' },
  { value: 'Sun', title: 'Sunday' },
];

const hoursOfDay = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  title: `${i < 10 ? '0' : ''}${i}:00`,
}));

interface Props {
  constraints: Constraints;
  onChange: (_: Constraints) => void;
  readOnly?: boolean;
  visibleConstraints?: ConstraintType[];
}

export default function ConstraintsForm({
  constraints,
  onChange,
  readOnly,
  visibleConstraints,
}: Props) {
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [selectKeywordsModalOpen, setSelectKeywordsModalOpen] = useState(false);

  const showKeywords =
    !visibleConstraints || visibleConstraints.includes('keywords');

  const showDevices =
    !visibleConstraints || visibleConstraints.includes('devices');

  const showAds = !visibleConstraints || visibleConstraints.includes('ads');

  const showAdsGroup =
    !visibleConstraints || visibleConstraints.includes('adGroups');

  const showCampaigns =
    !visibleConstraints || visibleConstraints.includes('campaigns');

  const showSchedules =
    !visibleConstraints || visibleConstraints.includes('schedules');

  const filters = useMemo(() => {
    if (!selectedDynamicPage?.data().connections) return;

    return Object.values(selectedDynamicPage?.data().connections ?? {}).map(
      (c) => ({
        ...(c.type === 'adsCampaign' ? { campaignId: c.id } : {}),
        adsAccountId: c.adsAccountId,
        provider: c.provider,
      }),
    );
  }, [selectedDynamicPage]);

  const constraintsChildren = [];

  if (showKeywords) {
    constraintsChildren.push(
      <ConstraintsTypeSection
        type="keywords"
        title="Keywords"
        tooltip={({ keyword }) => `Keyword: ${keyword}`}
        chipTitle={(item) => item.keyword}
        emptyMessage="No keywords selected"
        constraints={constraints}
        onChange={onChange}
        // filters={filters}
        selectButton={
          <Button
            className="h-full"
            variant="ternary"
            color="primary"
            size="small"
            onClick={() => {
              setSelectKeywordsModalOpen(true);
            }}
          >
            Select
          </Button>
        }
      />,
    );
  }

  if (showDevices) {
    constraintsChildren.push(
      <div className="mb-2 flex w-full flex-col gap-2">
        <p className="text-sm font-medium text-gray-900">Devices</p>
        <ToggleButton<DeviceConstraint | 'all'>
          multi
          disabled={readOnly}
          items={[
            { value: 'all', title: 'All Devices' },
            { value: 'mobile', title: 'Mobile', icon: DevicePhoneMobileIcon },
            { value: 'tablet', title: 'Tablet', icon: DeviceTabletIcon },
            { value: 'desktop', title: 'Desktop', icon: ComputerDesktopIcon },
          ]}
          value={!constraints.devices?.length ? ['all'] : constraints.devices}
          onChange={(newDevices) => {
            if (constraints.devices?.length && newDevices.includes('all')) {
              onChange({ ...constraints, devices: [] });
            } else {
              onChange({
                ...constraints,
                devices: newDevices
                  .filter((x) => x !== 'all')
                  .map((x) => x satisfies DeviceConstraint),
              });
            }
          }}
        />
      </div>,
    );
  }

  if (showSchedules) {
    constraintsChildren.push(
      <div className="mb-2 flex w-full flex-col gap-4">
        <div className="flex flex-row items-end justify-between">
          <p className="text-sm font-medium text-gray-900">Schedule</p>
          <Button
            variant="ternary"
            size="x-small"
            color={constraints.schedules?.length ? 'negative' : 'primary'}
            onClick={() => {
              if (constraints.schedules?.length) {
                onChange({
                  ...constraints,
                  schedules: [],
                });
                return;
              }
              const newSchedule = {
                id: uid(),
                timeZone: 'UTC',
              };

              onChange({
                ...constraints,
                schedules: [newSchedule],
              });
            }}
            disabled={readOnly}
          >
            {constraints.schedules?.length ? 'Remove Schedule' : 'Add Schedule'}
          </Button>
        </div>
        {constraints.schedules?.length ? (
          <div className="flex flex-col gap-4">
            <Autocomplete
              selected={constraints.schedules?.[0]?.timeZone ?? 'UTC'}
              onChange={(e) => {
                const newSchedule = {
                  id: uid(),
                  ...constraints.schedules?.[0],
                  timeZone: e || 'UTC',
                };

                onChange({
                  ...constraints,
                  schedules: [newSchedule],
                });
              }}
              options={timezonesList}
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <div className="flex-1">
                <TextField
                  type="date"
                  title="From Date (optional)"
                  titlePosition="overlapping"
                  value={constraints.schedules?.[0]?.startDate || ''}
                  onChange={(v) => {
                    const newSchedule = {
                      id: uid(),
                      timeZone: 'UTC',
                      ...constraints.schedules?.[0],
                      startDate: v.target.value?.split('T')[0] || (null as any),
                    };

                    onChange({
                      ...constraints,
                      schedules: [newSchedule],
                    });
                  }}
                />
              </div>
              <div className="flex-1">
                <TextField
                  type="time"
                  title="From Time"
                  titlePosition="overlapping"
                  value={constraints.schedules?.[0]?.startTime ?? '00:00'}
                  onChange={(v) => {
                    const newSchedule = {
                      id: uid(),
                      timeZone: 'UTC',
                      ...constraints.schedules?.[0],
                      startTime: v.target.value,
                    };

                    onChange({
                      ...constraints,
                      schedules: [newSchedule],
                    });
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <div className="flex-1">
                <TextField
                  type="date"
                  title="To Date (optional)"
                  titlePosition="overlapping"
                  value={constraints.schedules?.[0]?.endDate}
                  onChange={(v) => {
                    const newSchedule = {
                      id: uid(),
                      timeZone: 'UTC',
                      ...constraints.schedules?.[0],
                      endDate: v.target.value?.split('T')[0] || (null as any),
                    };

                    onChange({
                      ...constraints,
                      schedules: [newSchedule],
                    });
                  }}
                />
              </div>
              <div className="flex-1">
                <TextField
                  type="time"
                  title="To Time"
                  titlePosition="overlapping"
                  value={constraints.schedules?.[0]?.endTime ?? '23:59'}
                  onChange={(v) => {
                    const newSchedule = {
                      id: uid(),
                      timeZone: 'UTC',
                      ...constraints.schedules?.[0],
                      endTime: v.target.value,
                    };

                    onChange({
                      ...constraints,
                      schedules: [newSchedule],
                    });
                  }}
                />
              </div>
            </div>
            <ChipsAutocomplete
              title="Days of Week (optional)"
              titlePosition="overlapping"
              selected={constraints.schedules?.[0]?.daysOfWeek ?? []}
              onChange={(days) => {
                const newSchedule = {
                  id: uid(),
                  timeZone: 'UTC',
                  ...constraints.schedules?.[0],
                  daysOfWeek: days.length
                    ? days?.sort(
                        (a, b) =>
                          daysOfWeek.findIndex((d) => d.value === a) -
                          daysOfWeek.findIndex((d) => d.value === b),
                      )
                    : (null as any),
                };

                onChange({
                  ...constraints,
                  schedules: [newSchedule],
                });
              }}
              options={daysOfWeek.map((d) => d.value)}
              getValue={(o) => {
                return daysOfWeek.find((d) => d.value === o)?.title || o;
              }}
              disabled={readOnly}
            />
            <ChipsAutocomplete
              title="Hours of Day (optional)"
              titlePosition="overlapping"
              selected={constraints.schedules?.[0]?.hoursOfDay ?? []}
              onChange={(hours) => {
                const newSchedule = {
                  id: uid(),
                  timeZone: 'UTC',
                  ...constraints.schedules?.[0],
                  hoursOfDay: hours.length
                    ? hours.sort((a, b) => a - b)
                    : (null as any),
                };

                onChange({
                  ...constraints,
                  schedules: [newSchedule],
                });
              }}
              options={hoursOfDay.map((h) => h.value)}
              getValue={(o) => {
                return hoursOfDay.find((h) => h.value === o)?.title || '';
              }}
              disabled={readOnly}
            />
          </div>
        ) : (
          <div className="flex h-full w-full items-center">
            <p className="text-sm text-gray-500">No schedule set</p>
          </div>
        )}
      </div>,
    );
  }

  if (showAds) {
    constraintsChildren.push(
      <ConstraintsAdsAccountItemsSection
        type="ads"
        title="Ads"
        tooltip={(item) => {
          if (item.provider === 'facebook' || item.provider === 'google') {
            return <AdPreview ad={item} />;
          }
          return `Ad Id: ${(item as any).id}`;
        }}
        emptyMessage="No ads selected"
        constraints={constraints}
        onChange={onChange}
        filters={filters}
      />,
    );
  }

  if (showAdsGroup) {
    constraintsChildren.push(
      <ConstraintsAdsAccountItemsSection
        type="adGroups"
        title="Ad Groups"
        tooltip={({ id }) => `Ad Group Id: ${id}`}
        emptyMessage="No ad groups selected"
        constraints={constraints}
        onChange={onChange}
        filters={filters}
      />,
    );
  }

  if (showCampaigns) {
    constraintsChildren.push(
      <ConstraintsAdsAccountItemsSection
        type="campaigns"
        title="Campaigns"
        tooltip={({ id }) => `Campaign Id: ${id}`}
        emptyMessage="No campaigns selected"
        constraints={constraints}
        onChange={onChange}
        filters={filters}
      />,
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <KeywordsConstraintSelectorModal
        constraints={constraints}
        open={selectKeywordsModalOpen}
        onClose={() => {
          setSelectKeywordsModalOpen(false);
        }}
        onSave={onChange}
      />
      {constraintsChildren
        .flatMap((item, index, arr) => [
          item,
          index < arr.length - 1 ? (
            <div className="flex items-center gap-2">
              <div className="h-px grow bg-gray-200"></div>
              <div>
                <p className="text-xs font-medium text-gray-900 italic">and</p>
              </div>
              <div className="h-px grow bg-gray-200"></div>
            </div>
          ) : null,
        ])
        .filter(Boolean)}
    </div>
  );
}

const MAX_CHIPS = 10;

interface ConstraintsTypeSectionProps<T extends ConstraintType> {
  type: T;
  title: string;
  emptyMessage: string;
  tooltip:
    | string
    | ((item: NonNullable<Constraints[T]>[0]) => string | ReactNode);
  constraints: Constraints;
  chipTitle: (item: NonNullable<Constraints[T]>[0]) => string;
  onChange: (_: Constraints) => void;
  selectButton: ReactNode;
}

const ConstraintsTypeSection = <
  T extends Extract<
    ConstraintType,
    'campaigns' | 'adGroups' | 'ads' | 'keywords'
  >,
>({
  type,
  title,
  emptyMessage,
  tooltip,
  constraints,
  chipTitle,
  onChange,
  selectButton,
}: ConstraintsTypeSectionProps<T>) => {
  const selected = constraints[type];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full items-center justify-end gap-2">
        <div className="grow text-sm font-medium text-gray-900">{title}</div>
        {selectButton}
      </div>
      <div className="flex flex-1 flex-wrap gap-2">
        {!selected?.length && (
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        )}
        {selected?.slice(0, MAX_CHIPS).map((item) => (
          <Tooltip
            key={item.id}
            text={typeof tooltip === 'string' ? tooltip : tooltip(item)}
            color="white"
          >
            <Badge
              key={item.id}
              hideDot
              title={
                <>
                  {item?.provider === 'facebook' ? (
                    <img src={facebookAdsIcon} className="size-3" />
                  ) : (
                    <img src={googleAdsIcon} className="size-3" />
                  )}
                  {chipTitle(item)}
                </>
              }
              showDeleteButton
              onDelete={() => {
                onChange({
                  ...constraints,
                  [type]: selected?.filter((_item) => _item.id !== item.id),
                });
              }}
            />
          </Tooltip>
        ))}
        {(selected?.length ?? 0) > MAX_CHIPS && (
          <Popover>
            <PopoverButton
              as={Button}
              variant="ternary"
              size="x-small"
              color="primary"
            >
              Show More
            </PopoverButton>

            <PopoverPanel
              transition
              anchor="top end"
              modal
              className="z-[9999] h-80 w-96 transition duration-200 ease-in-out data-closed:translate-y-1 data-closed:opacity-0"
            >
              <div
                className="flex h-full flex-col divide-y divide-gray-200 rounded-lg border border-gray-300 bg-white p-3 py-2.5 pb-0 shadow-xs"
                style={{ height: 'calc(100% - 0.625rem)' }}
              >
                <div className="pb-2 font-medium">Selected {title}</div>
                <div className="flex flex-1 flex-col justify-stretch gap-2 overflow-scroll py-2">
                  {selected?.map((item) => (
                    <Button
                      key={item.id}
                      variant="secondary"
                      size="normal"
                      className="relative inline-block justify-start ps-4 pe-9 text-start"
                    >
                      <span>
                        {capitalize(
                          ('name' in item && item.name) ||
                            ('keyword' in item && item.keyword) ||
                            ('headlines' in item &&
                              item.headlines
                                .sort(
                                  (h1: any, h2: any) =>
                                    h1.metrics.impressions -
                                    h2.metrics.impressions,
                                )
                                .map((h: any) => h.text)
                                ?.slice(0, 3)
                                .join(' | ')) ||
                            'Ad',
                        )}
                      </span>{' '}
                      <span className="text-xs text-gray-400">- {item.id}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverPanel>
          </Popover>
        )}
      </div>
    </div>
  );
};

type ConstraintsAdsAccountItemsSectionProps<
  T extends Extract<ConstraintType, 'campaigns' | 'adGroups' | 'ads'>,
> = Omit<ConstraintsTypeSectionProps<T>, 'selectButton' | 'chipTitle'> & {
  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

const ConstraintsAdsAccountItemsSection = <
  T extends Extract<ConstraintType, 'campaigns' | 'adGroups' | 'ads'>,
>({
  type,
  title,
  emptyMessage,
  tooltip,
  constraints,
  onChange,
  filters,
}: ConstraintsAdsAccountItemsSectionProps<T>) => {
  return (
    <ConstraintsTypeSection
      type={type}
      title={title}
      emptyMessage={emptyMessage}
      tooltip={tooltip}
      chipTitle={(item) => getConstraintTitle(type, item)}
      constraints={constraints}
      onChange={onChange}
      selectButton={
        <AdsAccountsDataConstraintPopover
          type={type}
          selected={(constraints[type] || []) as any[]}
          onChange={(items: any) => {
            onChange({
              ...constraints,
              [type]: items,
            });
          }}
          filters={filters}
        />
      }
    />
  );
};

type AdsItemsConstraintSelectorCampaigns = {
  type: Extract<ConstraintType, 'campaigns'>;
  selected: AdsCampaignConstraint[];
  onChange: (_: AdsCampaignConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsItemsConstraintSelectorAdGroups = {
  type: Extract<ConstraintType, 'adGroups'>;
  selected: AdGroupConstraint[];
  onChange: (_: AdGroupConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsItemsConstraintSelectorAds = {
  type: Extract<ConstraintType, 'ads'>;
  selected: AdConstraint[];
  onChange: (_: AdConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsAccountsDataConstraintSelectorProps =
  | AdsItemsConstraintSelectorCampaigns
  | AdsItemsConstraintSelectorAdGroups
  | AdsItemsConstraintSelectorAds;

const AdsAccountsDataConstraintPopover = (
  props: AdsAccountsDataConstraintSelectorProps,
) => {
  return (
    <Popover className="relative h-full">
      <PopoverButton
        as={Button}
        className="h-full"
        variant="ternary"
        color="primary"
        size="small"
      >
        Select
      </PopoverButton>

      <PopoverPanel
        transition
        anchor="top end"
        modal
        className="z-[9999] h-80 w-96 transition duration-200 ease-in-out data-closed:translate-y-1 data-closed:opacity-0"
      >
        <div
          className="h-full rounded-lg border border-gray-300 bg-white p-3 py-2.5 pb-0 shadow-xs"
          style={{ height: 'calc(100% - 0.625rem)' }}
        >
          <AdsAccountsDataConstraint {...props} />
        </div>
      </PopoverPanel>
    </Popover>
  );
};

const AdsAccountsDataConstraint = ({
  type,
  selected,
  onChange,
  filters,
}: AdsAccountsDataConstraintSelectorProps) => {
  const { selectedAccount } = useContext(UserContext);

  const [campaigns, setCampaigns] = useState<AdsCampaign[]>();
  const [campaignAdGroups, setCampaignAdGroups] = useState<AdsGroup[]>();
  const [adGroupAds, setAdGroupAds] = useState<AdsGroupAd[]>();

  const [currentCampaign, setCurrentCampaign] = useState<AdsCampaign>();
  const [currentAdGroup, setCurrentAdGroup] = useState<AdsGroup>();
  const [error, setError] = useState<string>();

  const [showSearch, setShowSearch] = useState(false);
  const [searchQueries, setSearchQueries] = useState<
    Record<typeof type, string>
  >({} as any);

  const reset = useCallback(() => {
    setCurrentCampaign(undefined);
    setCurrentAdGroup(undefined);
    setCampaigns(undefined);
    setCampaignAdGroups(undefined);
    setAdGroupAds(undefined);
    setError(undefined);
  }, []);

  useEffect(() => {
    reset();
  }, [type, filters, reset]);

  useEffect(() => {
    setCampaigns(undefined);
    setError(undefined);

    if (!selectedAccount) return;

    getCachedCampaigns(
      selectedAccount.id,
      distinct(filters?.map((f) => f.provider) ?? ['google']),
      filters,
    )
      .then((data) => {
        setCampaigns(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch campaigns');
      });
  }, [filters, selectedAccount]);

  useEffect(() => {
    setCampaignAdGroups(undefined);
    setError(undefined);

    if (!selectedAccount || !currentCampaign) return;

    getCachedAdGroups(
      selectedAccount.id,
      currentCampaign.provider,
      currentCampaign.adsAccountId,
      [currentCampaign.id],
    )
      .then((data) => {
        setCampaignAdGroups(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch ad groups');
      });
  }, [currentCampaign, selectedAccount]);

  useEffect(() => {
    setAdGroupAds(undefined);
    setError(undefined);

    if (!selectedAccount || !currentAdGroup || !currentCampaign) return;

    getCachedAdGroupAds(
      selectedAccount.id,
      currentAdGroup.provider,
      currentAdGroup.adsAccountId,
      [currentCampaign.id],
      [currentAdGroup.id],
    )
      .then((data) => {
        setAdGroupAds(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch ads');
      });
  }, [currentCampaign, currentAdGroup, selectedAccount]);

  const currentLevel: 'campaigns' | 'adGroups' | 'ads' = useMemo(() => {
    return currentAdGroup ? 'ads' : currentCampaign ? 'adGroups' : 'campaigns';
  }, [currentCampaign, currentAdGroup]);

  const searchQuery = useMemo(
    () => searchQueries[currentLevel],
    [searchQueries, currentLevel],
  );

  const currentList = useMemo(() => {
    switch (currentLevel) {
      case 'campaigns':
        return campaigns?.filter((c) =>
          c.name.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
        );
      case 'adGroups':
        return campaignAdGroups?.filter((c) =>
          c.name.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
        );
      case 'ads':
        return adGroupAds?.filter(
          (
            c: Pick<Partial<GoogleAdsAdGroupAd>, 'headlines' | 'descriptions'> &
              Pick<Partial<FacebookAdsAdSetAd>, 'titles' | 'bodies'>,
          ) =>
            c.headlines?.some((h: any) =>
              h.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ) ||
            c.descriptions?.some((d: any) =>
              d.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ) ||
            c.titles?.some((t: any) =>
              t.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ) ||
            c.bodies?.some((b: any) =>
              b.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ),
        );
    }
  }, [currentLevel, campaigns, campaignAdGroups, adGroupAds, searchQuery]);

  return (
    <div className="relative flex h-full flex-col gap-2">
      <div
        className={classNames(
          'border-b border-b-gray-200',
          showSearch && 'pb-2',
          'w-full',
        )}
      >
        <div className="flex w-full justify-between gap-2">
          <Breadcrumbs
            mode="fit"
            className="flex-1 shrink overflow-hidden"
            breadcrumbs={[
              ...(currentLevel === 'campaigns'
                ? [
                    {
                      id: 'campaigns',
                      name: 'Campaigns',
                      href: '',
                      current: true,
                    },
                  ]
                : []),

              ...(currentLevel === 'adGroups'
                ? [
                    {
                      id: 'campaigns',
                      name: (
                        <div className="flex items-center gap-2">
                          {currentCampaign?.provider === 'facebook' ? (
                            <img src={facebookAdsIcon} className="h-4 w-4" />
                          ) : (
                            <img src={googleAdsIcon} className="h-4 w-4" />
                          )}
                          <span className="truncate">
                            {currentCampaign?.name ?? ''}
                          </span>
                        </div>
                      ),
                      href: '',
                      onClick: () => setCurrentCampaign(undefined),
                    },
                    {
                      id: 'adGroups',
                      name: 'Ad Groups',
                      href: '',
                      current: true,
                    },
                  ]
                : []),

              ...(currentLevel === 'ads'
                ? [
                    {
                      id: 'campaigns',
                      name: (
                        <div className="flex items-center gap-2">
                          {currentCampaign?.provider === 'facebook' ? (
                            <img src={facebookAdsIcon} className="h-4 w-4" />
                          ) : (
                            <img src={googleAdsIcon} className="h-4 w-4" />
                          )}
                          <span className="truncate">
                            {currentCampaign?.name ?? ''}
                          </span>
                        </div>
                      ),
                      href: '',
                      onClick: () => {
                        setCurrentAdGroup(undefined);
                        setCurrentCampaign(undefined);
                      },
                    },
                    {
                      id: 'adGroups',
                      name: currentAdGroup?.name ?? '',
                      href: '',
                      onClick: () => setCurrentAdGroup(undefined),
                    },
                    { id: 'ads', name: 'Ads', href: '', current: true },
                  ]
                : []),
            ]}
          />

          <IconButton
            className="relative"
            variant="ternary"
            onClick={() => {
              setShowSearch((x) => !x);
            }}
          >
            <MagnifyingGlassIcon className="size-4 text-gray-600" />
          </IconButton>
        </div>

        {showSearch && (
          <SearchField
            value={searchQuery || ''}
            onChange={(e) => {
              setSearchQueries((d) => ({
                ...d,
                [currentLevel]: e.target.value,
              }));
            }}
          />
        )}
      </div>

      <div className="flex h-full w-full flex-col gap-1 overflow-scroll pb-3">
        {!currentList && !error && (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <Loader />
          </div>
        )}
        {!currentList && !!error && (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <p className="text-center text-red-500">{error}</p>
          </div>
        )}
        {currentList?.length === 0 && <p>No items found</p>}
        {currentList?.map((item) => {
          const itemSeleted = selected.some((s) => s.id === item.id);
          return (
            <Button
              key={item.id}
              variant={itemSeleted ? 'primary' : 'secondary'}
              size="normal"
              onClick={() => {
                if (currentLevel === type) {
                  onChange(
                    itemSeleted
                      ? selected.filter((t) => t.id !== item.id)
                      : ([...selected, item] as any),
                  );
                } else if (currentLevel === 'campaigns') {
                  setCurrentCampaign(item as AdsCampaign);
                } else if (currentLevel === 'adGroups') {
                  setCurrentAdGroup(item as AdsGroup);
                }
              }}
              className="relative justify-start ps-4 pe-9 text-center"
            >
              {currentLevel === type ? (
                <CheckIcon
                  className={classNames(
                    'absolute right-2 h-4 w-4',
                    itemSeleted ? 'visible' : 'invisible',
                  )}
                />
              ) : (
                <ChevronRightIcon
                  className={classNames(
                    'absolute right-2 h-4 w-4 text-gray-400',
                  )}
                />
              )}

              {currentLevel === 'campaigns' && (
                <>
                  {item.provider === 'facebook' ? (
                    <img src={facebookAdsIcon} className="size-3" />
                  ) : (
                    <img src={googleAdsIcon} className="size-3" />
                  )}
                </>
              )}

              <span className="text-start">
                {capitalize(
                  getConstraintTitle(currentLevel, item),
                  // ('name' in item && item.name) ||
                  //   ('headlines' in item &&
                  //     item.headlines
                  //       .sort(
                  //         (h1: any, h2: any) =>
                  //           h1.metrics.impressions - h2.metrics.impressions,
                  //       )
                  //       .map((h: any) => h.text)
                  //       ?.slice(0, 3)
                  //       .join(' | ')) ||
                  //   'Ad',
                )}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
